const SIDEBAR_PAGE_TTL = 2 * 60 * 1000;

const SIDEBAR_PAGE_IDS = {
  CONTACTS_DEFAULT: 'contacts-default',
  COMPANIES_DEFAULT: 'companies-default',
  NOTIFICATIONS_DEFAULT: 'notifications-default',
  CANNED_DEFAULT: 'canned-default',
  CAPTAIN_ASSISTANTS_DEFAULT: 'captain-assistants-default',
  CAPTAIN_RESPONSES_DEFAULT: 'captain-responses-default',
  CAPTAIN_DOCUMENTS_DEFAULT: 'captain-documents-default',
};

const sidebarPageCache = new Map();
const inflightPreloads = new Map();
const ACELERES_FUNIL_FRAME_ID = 'aceleres-funil-preload-frame';
const buildAceleresFunilFrameSrc = () =>
  `/funil?aceleres_sso_retry=${Date.now()}`;

let preloadedScopeKey = null;
let funilFrameLoaded = false;

const getNow = () => Date.now();

const buildInflightKey = (pageId, cacheKey) => `${pageId}:${cacheKey}`;

const scheduleSoon = callback => {
  if (typeof window === 'undefined') return Promise.resolve().then(callback);

  return new Promise(resolve => {
    const runner = async () => {
      try {
        await callback();
      } finally {
        resolve();
      }
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runner, { timeout: 300 });
      return;
    }

    window.setTimeout(runner, 0);
  });
};

const hideAceleresFunilFrame = frame => {
  Object.assign(frame.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
    border: '0',
  });
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
};

const showAceleresFunilFrame = frame => {
  Object.assign(frame.style, {
    position: 'static',
    left: '',
    top: '',
    width: '100%',
    height: '100%',
    opacity: '1',
    pointerEvents: 'auto',
    border: '0',
    display: 'block',
  });
  frame.removeAttribute('aria-hidden');
  frame.removeAttribute('tabindex');
};

const getOrCreateAceleresFunilFrame = ({ onLoad, onError } = {}) => {
  if (typeof document === 'undefined') return null;

  let frame = document.getElementById(ACELERES_FUNIL_FRAME_ID);
  let isNewFrame = false;
  if (!frame) {
    frame = document.createElement('iframe');
    frame.id = ACELERES_FUNIL_FRAME_ID;
    frame.title = 'Carteirização';
    frame.loading = 'eager';
    hideAceleresFunilFrame(frame);
    document.body.appendChild(frame);
    isNewFrame = true;
  }

  frame.onload = () => {
    funilFrameLoaded = true;
    onLoad?.();
  };
  frame.onerror = () => {
    onError?.();
  };

  if (isNewFrame) {
    frame.src = buildAceleresFunilFrameSrc();
  }

  return frame;
};

export const refreshAceleresFunilFrame = frame => {
  if (!frame) return;

  funilFrameLoaded = false;
  frame.src = buildAceleresFunilFrameSrc();
};

export const preloadAceleresFunilFrame = () => {
  scheduleSoon(() => {
    getOrCreateAceleresFunilFrame();
  });
};

export const mountAceleresFunilFrame = (
  container,
  { onLoad, onError } = {}
) => {
  if (!container) return false;

  const frame = getOrCreateAceleresFunilFrame({ onLoad, onError });
  if (!frame) return false;

  // O preloader pode ter executado o SSO antes de a sessão atual estar pronta
  // ou enquanto uma sessão anterior ainda estava no cookie. Reexecutar a
  // página ao abrir garante que a ponte Zoraxy -> Aceleres use os tokens atuais.
  refreshAceleresFunilFrame(frame);
  showAceleresFunilFrame(frame);
  container.appendChild(frame);

  if (funilFrameLoaded) {
    window.setTimeout(() => onLoad?.(), 0);
  }

  return true;
};

export const parkAceleresFunilFrame = () => {
  if (typeof document === 'undefined') return;

  const frame = document.getElementById(ACELERES_FUNIL_FRAME_ID);
  if (!frame) return;

  hideAceleresFunilFrame(frame);
  document.body.appendChild(frame);
};

export const markSidebarPageReady = (pageId, cacheKey) => {
  sidebarPageCache.set(pageId, {
    cacheKey,
    loadedAt: getNow(),
  });
};

export const isSidebarPageReady = (
  pageId,
  cacheKey,
  maxAgeMs = SIDEBAR_PAGE_TTL
) => {
  const cachedPage = sidebarPageCache.get(pageId);

  if (!cachedPage || cachedPage.cacheKey !== cacheKey) {
    return false;
  }

  return getNow() - cachedPage.loadedAt <= maxAgeMs;
};

const runPreloadTask = async ({ pageId, cacheKey, load }) => {
  const inflightKey = buildInflightKey(pageId, cacheKey);
  if (inflightPreloads.has(inflightKey)) {
    return inflightPreloads.get(inflightKey);
  }

  const pendingTask = (async () => {
    try {
      await load();
      markSidebarPageReady(pageId, cacheKey);
    } catch (error) {
      // Ignore preload failures. Route navigation still uses the normal fetch.
    } finally {
      inflightPreloads.delete(inflightKey);
    }
  })();

  inflightPreloads.set(inflightKey, pendingTask);
  return pendingTask;
};

export const buildContactsDefaultSidebarPageKey = ({
  sortAttr = 'last_activity_at',
} = {}) => JSON.stringify({ page: 1, sortAttr });

export const buildCompaniesDefaultSidebarPageKey = ({ sort = 'name' } = {}) =>
  JSON.stringify({ page: 1, sort });

export const buildNotificationsDefaultSidebarPageKey = () =>
  JSON.stringify({ page: 1 });

export const buildCannedDefaultSidebarPageKey = () =>
  JSON.stringify({ scope: 'default' });

export const buildCaptainAssistantsSidebarPageKey = () =>
  JSON.stringify({ scope: 'default' });

export const buildCaptainResponsesSidebarPageKey = ({
  assistantId,
  page = 1,
  status = 'approved',
  search = '',
} = {}) =>
  JSON.stringify({ assistantId: Number(assistantId), page, status, search });

export const buildCaptainDocumentsSidebarPageKey = ({
  assistantId,
  page = 1,
} = {}) => JSON.stringify({ assistantId: Number(assistantId), page });

const resolveCaptainAssistantId = ({ store, preferredAssistantId }) => {
  const assistants = store.getters['captainAssistants/getRecords'] || [];
  if (!assistants.length) return null;

  const normalizedPreferredId = Number(preferredAssistantId);
  const preferredAssistant = assistants.find(
    assistant => assistant.id === normalizedPreferredId
  );

  if (preferredAssistant) {
    return preferredAssistant.id;
  }

  return assistants[0]?.id || null;
};

export const preloadSidebarPages = ({
  store,
  companiesStore,
  uiSettings = {},
  scopeKey = 'default',
} = {}) => {
  if (preloadedScopeKey === scopeKey) return;
  preloadedScopeKey = scopeKey;

  scheduleSoon(async () => {
    const contactsKey = buildContactsDefaultSidebarPageKey({
      sortAttr: uiSettings.contacts_sort_by || 'last_activity_at',
    });
    const companiesKey = buildCompaniesDefaultSidebarPageKey({
      sort: uiSettings.companies_sort_by || 'name',
    });
    const notificationsKey = buildNotificationsDefaultSidebarPageKey();
    const cannedKey = buildCannedDefaultSidebarPageKey();
    const assistantsKey = buildCaptainAssistantsSidebarPageKey();

    await Promise.allSettled([
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CONTACTS_DEFAULT,
        cacheKey: contactsKey,
        load: () =>
          store.dispatch('contacts/get', {
            page: 1,
            sortAttr: uiSettings.contacts_sort_by || 'last_activity_at',
          }),
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.COMPANIES_DEFAULT,
        cacheKey: companiesKey,
        load: () =>
          companiesStore.get({
            page: 1,
            sort: uiSettings.companies_sort_by || 'name',
          }),
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.NOTIFICATIONS_DEFAULT,
        cacheKey: notificationsKey,
        load: () => store.dispatch('notifications/get', { page: 1 }),
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CANNED_DEFAULT,
        cacheKey: cannedKey,
        load: () => store.dispatch('getCannedResponse'),
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CAPTAIN_ASSISTANTS_DEFAULT,
        cacheKey: assistantsKey,
        load: () => store.dispatch('captainAssistants/get'),
      }),
    ]);

    const assistantId = resolveCaptainAssistantId({
      store,
      preferredAssistantId: uiSettings.last_active_assistant_id,
    });

    if (!assistantId) return;

    await Promise.allSettled([
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CAPTAIN_RESPONSES_DEFAULT,
        cacheKey: buildCaptainResponsesSidebarPageKey({ assistantId }),
        load: async () => {
          await store.dispatch('captainResponses/get', {
            assistantId,
            page: 1,
            status: 'approved',
          });
          await store.dispatch(
            'captainResponses/fetchPendingCount',
            assistantId
          );
        },
      }),
      runPreloadTask({
        pageId: SIDEBAR_PAGE_IDS.CAPTAIN_DOCUMENTS_DEFAULT,
        cacheKey: buildCaptainDocumentsSidebarPageKey({ assistantId }),
        load: () =>
          store.dispatch('captainDocuments/get', {
            assistantId,
            page: 1,
          }),
      }),
    ]);
  });
};

export { SIDEBAR_PAGE_IDS };
